/**
 * Public landing pages after a Stripe Checkout return for an Additional
 * Service order. Two routes share this component via the `kind` prop:
 *   - /services/thanks    → kind="paid"
 *   - /services/cancelled → kind="cancelled"
 *
 * The actual fulfillment is webhook-driven (billing.py →
 * handle_service_payment_completed). These pages just give the customer
 * a friendly confirmation and a way back into the app.
 */
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight, Mail } from "lucide-react";
import MarketingShell from "@/pages/compare/MarketingShell";

export default function ServiceStatusPage({ kind }) {
  const [params] = useSearchParams();
  const inquiryId = params.get("inquiry") || "";
  const paid = kind === "paid";

  return (
    <MarketingShell>
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div
          className="w-full max-w-xl bg-white border border-slate-200 rounded-2xl shadow-xl p-8 text-center"
          data-testid={paid ? "service-thanks-page" : "service-cancelled-page"}
        >
          {paid ? (
            <CheckCircle2 className="mx-auto mb-3 text-emerald-500" size={56} />
          ) : (
            <XCircle className="mx-auto mb-3 text-amber-500" size={56} />
          )}

          <h1 className="font-display font-extrabold text-3xl text-slate-900">
            {paid ? "Payment received 🎉" : "Checkout cancelled"}
          </h1>

          <p className="text-slate-600 mt-3 leading-relaxed">
            {paid
              ? "Thank you — your order is in. We just emailed you the next-step details and Our Team will be in touch within one business day to kick off fulfillment."
              : "No worries — nothing was charged. If you'd like a custom quote, a bundle, or just have a question, reach out and we'll sort it out."}
          </p>

          {inquiryId && (
            <div className="mt-4 text-[11px] text-slate-400">
              Reference: <code>{inquiryId}</code>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/v3"
              className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg px-5 py-2.5 transition"
              data-testid="service-status-back-to-app"
            >
              Back to app <ArrowRight size={16} />
            </Link>
            <a
              href="mailto:hello@ispeedtoclose.com"
              className="inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold rounded-lg px-5 py-2.5 transition"
              data-testid="service-status-contact"
            >
              <Mail size={16} /> Email us
            </a>
          </div>
        </div>
      </div>
    </MarketingShell>
  );
}
